import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Sun, Loader2, ArrowRight } from "lucide-react";
import { getCurrency, fmtMoney } from "../../lib/currency";
import { getBookingSettings } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const SLOTS = [
  { v: "10:00", label: "10 AM" },
  { v: "17:00", label: "5 PM" },
  { v: "21:00", label: "9 PM" },
];
const PASSES = [
  { id: "day", label: "Day Pass" },
  { id: "exclusive", label: "Exclusive Pass" },
  { id: "overnight", label: "Overnight Pass" },
];
const SESSIONS = [
  { id: "morning", label: "Morning" },
  { id: "afternoon", label: "Afternoon" },
  { id: "fullday", label: "Full-day" },
];
const SESSION_LABEL = { morning: "Morning", afternoon: "Afternoon", fullday: "Full-day", overnight: "8PM – 8AM" };
const PASS_DESC = {
  day: "Reserve a seat. Co-work with us, read a book, or see our process.",
  exclusive: "Exclusive use of the studio for product presentations, pop-ups, or workshops.",
  overnight: "Includes bed and breakfast for visiting guests.",
};
const PRICING = {
  usd: {
    day: { morning: 15, afternoon: 15, fullday: 25 },
    exclusive: { morning: 200, afternoon: 200, fullday: 350 },
    overnight: { overnight: 500 },
  },
  php: {
    day: { morning: 300, afternoon: 300, fullday: 500 },
    exclusive: { morning: 3000, afternoon: 3000, fullday: 5000 },
    overnight: { overnight: 2000 },
  },
};
const DISCOUNTS = { RADA10: 0.1, STUDIO20: 0.2, OPEN15: 0.15 };
const DAY_SEAT_CAP = 6;

export const Booking = () => {
  // "today" follows Philippine time (slots are PHT), not the visitor's timezone
  const phtNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  const today = new Date(phtNow.getFullYear(), phtNow.getMonth(), phtNow.getDate());

  const [type, setType] = useState("meeting");
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [date, setDate] = useState(null);            // single date (studio current / meeting)
  const [staged, setStaged] = useState([]);          // studio staged: [{date, pass_type, session, guests}]
  const [time, setTime] = useState(null);
  const [customTime, setCustomTime] = useState("");
  const [customHours, setCustomHours] = useState(1);
  const [passType, setPassType] = useState("day");
  const [session, setSession] = useState(null);
  const [guests, setGuests] = useState(1);
  const [overnightHover, setOvernightHover] = useState(false);
  const [code, setCode] = useState("");
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [leadDays, setLeadDays] = useState(2);

  useEffect(() => {
    getBookingSettings().then((s) => setLeadDays(s.lead_time_days ?? 2)).catch(() => {});
  }, []);

  const monthLabel = view.toLocaleString("default", { month: "long", year: "numeric" });
  const firstDay = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));

  const minDate = new Date(today);
  minDate.setDate(today.getDate() + leadDays); // admin-configurable lead time
  const isWeekend = (dt) => dt.getDay() === 0 || dt.getDay() === 6;
  const weekendAllowed = type === "studio" && passType === "overnight";
  const isBlocked = (dt) => dt < minDate || (isWeekend(dt) && !weekendAllowed);
  const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const canPrev = view > new Date(today.getFullYear(), today.getMonth(), 1);
  const shiftMonth = (n) => setView(new Date(view.getFullYear(), view.getMonth() + n, 1));
  const iso = (dt) => dt.toLocaleDateString("en-CA");

  const loadAvail = (d) => {
    axios.get(`${API}/studio/availability`, { params: { date: iso(d) } })
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
  };

  useEffect(() => {
    if (type === "studio" && date) loadAvail(date);
    if (type === "meeting" && date) loadAvail(date); // busy-slot blocking for consult
  }, [type, date]);

  const { code: curCode, symbol } = getCurrency();
  const sessKey = passType === "overnight" ? "overnight" : session;
  let base = type === "meeting" ? 0 : (sessKey ? (PRICING[curCode][passType]?.[sessKey] || 0) : 0);
  if (passType === "overnight" && guests > 2) {
    base += (curCode === "php" ? 800 : 15) * (guests - 2); // base covers 1-2 guests
  }
  const disc = DISCOUNTS[code.toUpperCase().trim()] || 0;
  const total = Math.round(base * (1 - disc) * 100) / 100;
  const fmt = (n) => (Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2));
  // meeting: custom-time price (₱1,600/hr, +₱800 per extra 30 min)
  const meetingCustomPrice = customTime ? (curCode === "php" ? 1600 + 1600 * (customHours - 1) : 30 + 30 * (customHours - 1)) : 0;
  // custom-time hours: hide free-consult reserved slots (10AM, 5PM, 9PM) and gcal-busy hours
  const RESERVED = [10, 17, 21];
  const customTimeSlots = Array.from({ length: 17 }, (_, i) => `${7 + i}:00`)
    .filter((s) => !RESERVED.includes(parseInt(s, 10)) && !(avail?.busy || []).includes(parseInt(s, 10)));

  // studio staged total
  const entryPrice = (e) => {
    let b = PRICING[curCode][e.pass_type]?.[e.session] || 0;
    if (e.pass_type === "overnight" && (e.guests || 1) > 2) b += (curCode === "php" ? 800 : 15) * (e.guests - 2);
    return b;
  };
  const currentEntry = { pass_type: passType, session: sessKey, guests };
  const currentPrice = sessKey ? entryPrice(currentEntry) : 0;
  const stagedTotal = staged.reduce((a, e) => a + entryPrice(e), 0);
  const grandTotal = Math.round((stagedTotal + (staged.length > 0 ? 0 : currentPrice)) * (1 - disc) * 100) / 100;

  const daySeats = (s) => {
    if (!avail) return DAY_SEAT_CAP;
    if (s === "fullday") {
      return Math.min(avail.day?.morning?.seats_left ?? DAY_SEAT_CAP, avail.day?.afternoon?.seats_left ?? DAY_SEAT_CAP);
    }
    return avail.day?.[s]?.seats_left ?? DAY_SEAT_CAP;
  };
  const sessDisabled = (s) => {
    if (!avail) return false;
    if (passType === "day") return !avail.day?.[s]?.available;
    if (passType === "exclusive") return !avail.exclusive?.[s];
    return false;
  };
  // Day pass duration is unavailable when any exclusive pass is booked; exclusive duration is unavailable when any day pass is booked.
  const dayBlocked = passType === "day" && avail?.day_blocked_all;
  const exclBlocked = passType === "exclusive" && avail?.day && ["morning", "afternoon", "fullday"].some((s) => (avail.day?.[s]?.seats_left ?? DAY_SEAT_CAP) < DAY_SEAT_CAP);

  const submit = async (e) => {
    e.preventDefault();
    if (type === "studio" && staged.length === 0 && !(date && sessKey)) return toast.error("Please add at least one date.");
    if (type === "meeting" && !date) return toast.error("Please pick a date.");
    if (type === "meeting" && !time && !customTime) return toast.error("Please pick a time.");
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    setBusy(true);
    try {
      if (type === "studio") {
        const entries = staged.length > 0
          ? [...staged, ...(date && sessKey ? [currentEntry] : [])].map((x) => ({ ...x, date: x.date || iso(date) }))
          : [{ date: iso(date), ...currentEntry }];
        for (const en of entries) {
          await axios.post(`${API}/bookings`, {
            type, date: en.date, time: null,
            pass_type: en.pass_type, session: en.session,
            discount_code: code || null, currency: curCode,
            name: form.name, email: form.email, note: form.note,
            guests: en.pass_type === "overnight" ? en.guests : null,
          });
        }
        toast.success(`Studio pass reserved for ${entries.length} date(s)${grandTotal ? ` · ${fmtMoney(symbol, grandTotal)}` : ""} — see you soon!`);
        setStaged([]); setSession(null); setCode("");
      } else {
        await axios.post(`${API}/bookings`, {
          type, date: iso(date),
          time: customTime ? `${customTime} (${customHours}h custom)` : time,
          custom_time: customTime || null, custom_hours: customTime ? customHours : null,
          pass_type: null, session: null,
          discount_code: code || null, currency: curCode,
          name: form.name, email: form.email, note: form.note, guests: null,
        });
        toast.success(customTime ? `Consult requested at ${customTime} for ${customHours}h · ${fmtMoney(symbol, meetingCustomPrice)} — we'll confirm by email.` : "Free consult requested — we'll confirm by email.");
        setTime(null); setCustomTime(""); setCustomHours(1); setCode("");
      }
      setForm({ name: "", email: "", note: "" });
      setDate(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't book. Please try again.");
    }
    setBusy(false);
  };

  const TypeBtn = ({ id, icon: Icon, label, sub }) => (
    <button
      type="button"
      data-testid={`booking-type-${id}`}
      onClick={() => { setType(id); setTime(null); setSession(null); setCustomTime(""); }}
      className={`flex-1 flex items-start gap-2.5 rounded-xl border p-3 text-left transition-[background,border-color,color] duration-200 ${
        type === id
          ? "bg-[#D7E85C] border-[#D7E85C] text-[#1F3864]"
          : "bg-transparent border-[#EBE6E0]/20 text-[#EBE6E0] hover:border-[#D7E85C]"
      }`}
    >
      <Icon size={18} className="mt-0.5 shrink-0" />
      <span>
        <span className="block font-display font-bold text-sm leading-none">{label}</span>
        <span className={`block text-[11px] mt-1 ${type === id ? "text-[#1F3864]/70" : "text-[#EBE6E0]/55"}`}>{sub}</span>
      </span>
    </button>
  );

  const chip = (selected, disabled) =>
    `px-3 py-1.5 rounded-full text-xs font-bold border transition-[background,border-color,color] duration-150 ${
      disabled
        ? "border-[#EBE6E0]/10 text-[#EBE6E0]/25 cursor-not-allowed"
        : selected
        ? "bg-[#D7E85C] border-[#D7E85C] text-[#1F3864]"
        : "border-[#EBE6E0]/20 text-[#EBE6E0] hover:border-[#D7E85C]"
    }`;

  const inputCls =
    "w-full bg-[#0f1d38] border border-[#EBE6E0]/20 rounded-lg px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 focus:outline-none focus:border-[#D7E85C] transition-colors";

  return (
    <div
      data-testid="booking-app"
      className="mt-10 rounded-3xl border border-[#D7E85C] bg-[#16294B] p-4 md:p-6 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)]"
    >
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="flex gap-2.5 md:w-2/3">
          <TypeBtn id="meeting" icon={CalendarDays} label="Book a FREE consult" sub="30-min discovery call" />
          <TypeBtn id="studio" icon={Sun} label="Studio Pass" sub="Co-work with us or book the studio for exclusive use." />
        </div>
        <div className="md:flex-1 flex items-center rounded-xl bg-[#D7E85C]/10 border border-[#D7E85C]/25 px-3.5 py-2.5 text-sm text-[#EBE6E0]">
          {type === "studio" ? (
            staged.length > 0 ? (
              <span data-testid="booking-summary">
                <strong>{PASSES.find((p) => p.id === passType)?.label}</strong> ·{" "}
                {staged.map((e) => new Date(e.date + "T00:00:00").toLocaleDateString("default", { month: "short", day: "numeric" })).join(", ")}
                {" "}({staged.length} date{staged.length > 1 ? "s" : ""})
              </span>
            ) : date ? (
              <span data-testid="booking-summary">
                <strong>{PASSES.find((p) => p.id === passType)?.label}</strong> ·{" "}
                {date.toLocaleDateString("default", { weekday: "short", month: "short", day: "numeric" })}
                {sessKey ? ` · ${SESSION_LABEL[sessKey]}` : ""}
              </span>
            ) : (
              <span className="text-[#EBE6E0]/55">Select a date to continue.</span>
            )
          ) : date ? (
            <span data-testid="booking-summary">
              <strong>{customTime ? "PAID consult" : "FREE consult"}</strong> ·{" "}
              {date.toLocaleDateString("default", { weekday: "short", month: "short", day: "numeric" })}
              {time ? ` · ${SLOTS.find((s) => s.v === time)?.label} PHT` : customTime ? ` · ${customTime} (${customHours}h)` : ""}
            </span>
          ) : (
            <span className="text-[#EBE6E0]/55">Select a date to continue.</span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
        {/* calendar */}
        <div className="rounded-xl border border-[#EBE6E0]/15 bg-[#0f1d38] p-3">
          <div className="flex items-center justify-between mb-2">
            <button type="button" data-testid="booking-prev-month" disabled={!canPrev} onClick={() => shiftMonth(-1)}
              className="grid place-items-center h-7 w-7 rounded-full text-[#EBE6E0] hover:bg-[#D7E85C]/20 disabled:opacity-30 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="font-display font-bold text-sm text-[#EBE6E0]" data-testid="booking-month-label">{monthLabel}</span>
            <button type="button" data-testid="booking-next-month" onClick={() => shiftMonth(1)}
              className="grid place-items-center h-7 w-7 rounded-full text-[#EBE6E0] hover:bg-[#D7E85C]/20 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
            {DOW.map((d) => <span key={d} className="text-[10px] font-bold uppercase text-[#EBE6E0]/40">{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((dt, i) =>
              dt === null ? <span key={i} /> : (
                <button key={i} type="button" data-testid={`booking-day-${dt.getDate()}`} disabled={isBlocked(dt)}
                  onClick={() => {
                    setDate(dt);
                    setSession(null);
                    if (type === "studio") loadAvail(dt);
                  }}
                  className={`h-7 rounded-md text-xs font-semibold transition-[background,color] duration-150 ${
                    sameDay(dt, date) ? "bg-[#D7E85C] text-[#1F3864]"
                    : type === "studio" && staged.some((e) => sameDay(new Date(e.date + "T00:00:00"), dt)) ? "bg-[#EBC9B5]/60 text-[#1F3864]"
                    : isBlocked(dt) ? "text-[#EBE6E0]/25 cursor-not-allowed"
                    : "text-[#EBE6E0] hover:bg-[#D7E85C]/20"}`}>
                  {dt.getDate()}
                </button>
              )
            )}
          </div>
          <p className="mt-2 text-[10px] leading-snug text-[#EBE6E0]/40" data-testid="booking-calendar-note">
            {leadDays === 0 ? "Same-day bookings are open." : `Earliest booking is ${leadDays} day${leadDays === 1 ? "" : "s"} from today.`} Saturdays &amp; Sundays are reserved for Overnight Pass stays.
          </p>
        </div>

        {/* options — beside the calendar */}
        <div>
          {type === "meeting" ? (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50 mb-2">Select time for free consult (PHT)</p>
              <div className="flex flex-wrap gap-2">
                {SLOTS.map((s) => (
                  <button key={s.v} type="button" data-testid={`booking-slot-${s.v}`} onClick={() => { setTime(s.v); setCustomTime(""); }}
                    className={chip(time === s.v, false)}>{s.label}</button>
                ))}
              </div>

              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50 mt-4 mb-2">
                Select preferred time (7AM – 12MN PHT)
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid="custom-time-select" value={customTime}
                  onChange={(e) => { setCustomTime(e.target.value); if (e.target.value) setTime(null); }}
                  className="bg-[#0f1d38] border border-[#EBE6E0]/25 text-[#EBE6E0] rounded-full px-4 py-1.5 text-xs font-bold uppercase outline-none focus:border-[#D7E85C] cursor-pointer">
                  <option value="">Choose hour…</option>
                  {customTimeSlots.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                {customTime && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50">Hours</span>
                    <button type="button" data-testid="custom-hours-minus" onClick={() => setCustomHours((h) => Math.max(1, Math.round((h - 0.5) * 2) / 2))}
                      className="h-6 w-6 rounded-full border border-[#EBE6E0]/25 text-[#EBE6E0] grid place-items-center text-sm">−</button>
                    <span data-testid="custom-hours-count" className="w-8 text-center font-display font-bold text-[#EBE6E0] text-sm">{customHours}h</span>
                    <button type="button" data-testid="custom-hours-plus" onClick={() => setCustomHours((h) => Math.min(2, Math.round((h + 0.5) * 2) / 2))}
                      className="h-6 w-6 rounded-full border border-[#EBE6E0]/25 text-[#EBE6E0] grid place-items-center text-sm">+</button>
                  </div>
                )}
              </div>
              {customTime && (
                <div data-testid="custom-time-total" className="mt-3 flex items-center justify-between rounded-lg bg-[#D7E85C]/10 border border-[#D7E85C]/25 px-3.5 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#EBE6E0]/70">Total · {customTime} · {customHours}h</span>
                  <span className="font-display font-black text-2xl text-[#D7E85C]">{fmtMoney(symbol, meetingCustomPrice)}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50 mb-2">Choose a pass</p>
              <div className="flex flex-wrap gap-2">
                {PASSES.map((p) => (
                  <button key={p.id} type="button" data-testid={`booking-pass-${p.id}`}
                    onClick={() => {
                      setPassType(p.id);
                      setSession(p.id === "overnight" ? "overnight" : null);
                      if (p.id !== "overnight") {
                        if (date && isWeekend(date)) { setDate(null); setAvail(null); }
                        setStaged((s) => s.filter((e) => !isWeekend(new Date(e.date + "T00:00:00"))));
                      }
                    }}
                    onMouseEnter={() => p.id === "overnight" && setOvernightHover(true)}
                    onMouseLeave={() => p.id === "overnight" && setOvernightHover(false)}
                    className={chip(passType === p.id, p.id === "overnight" && avail && !avail.overnight.available)}>
                    {p.label}
                  </button>
                ))}
              </div>
              <p data-testid="pass-desc" className="mt-2 text-[11px] font-bold text-[#D7E85C] min-h-[16px]">
                {PASS_DESC[passType]}
              </p>

              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50 mt-4 mb-2">Duration</p>
              <div className="flex flex-wrap items-center gap-2">
                {passType === "overnight" ? (
                  <>
                    <button type="button" data-testid="booking-session-overnight" className={chip(true, false)}>8PM – 8AM</button>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50">Guests</span>
                      <button type="button" data-testid="guests-minus" aria-label="Decrease guests"
                        onClick={() => setGuests((g) => Math.max(1, g - 1))}
                        className="h-7 w-7 rounded-full border border-[#EBE6E0]/25 text-[#EBE6E0] hover:border-[#D7E85C] grid place-items-center">−</button>
                      <span data-testid="guests-count" className="w-6 text-center font-display font-bold text-[#EBE6E0]">{guests}</span>
                      <button type="button" data-testid="guests-plus" aria-label="Increase guests"
                        onClick={() => setGuests((g) => Math.min(4, g + 1))}
                        className="h-7 w-7 rounded-full border border-[#EBE6E0]/25 text-[#EBE6E0] hover:border-[#D7E85C] grid place-items-center">+</button>
                    </div>
                  </>
                ) : passType === "day" && dayBlocked ? (
                  <div data-testid="duration-unavailable" className="px-3 py-2 rounded-lg border border-[#EBE6E0]/10 bg-[#EBE6E0]/5 text-[#EBE6E0]/40 text-xs font-bold uppercase tracking-widest">
                    Unavailable
                  </div>
                ) : passType === "exclusive" && exclBlocked ? (
                  <div data-testid="duration-unavailable" className="px-3 py-2 rounded-lg border border-[#EBE6E0]/10 bg-[#EBE6E0]/5 text-[#EBE6E0]/40 text-xs font-bold uppercase tracking-widest">
                    Unavailable
                  </div>
                ) : (
                  SESSIONS.map((s) => {
                    const dis = sessDisabled(s.id);
                    return (
                      <button key={s.id} type="button" data-testid={`booking-session-${s.id}`} disabled={dis}
                        onClick={() => setSession(s.id)} className={chip(session === s.id, dis)}>
                        {s.label}
                        {passType === "day" && avail ? <span className="ml-1 opacity-70">({daySeats(s.id)} left)</span> : null}
                      </button>
                    );
                  })
                )}
              </div>

              {type === "studio" && date && sessKey && (
                <button type="button" data-testid="add-date-btn" onClick={() => {
                    if (staged.some((e) => e.date === iso(date))) { toast.error("That date is already added."); return; }
                    setStaged([...staged, { date: iso(date), pass_type: passType, session: sessKey, guests: passType === "overnight" ? guests : 1 }]);
                    setDate(null); setSession(null); setAvail(null);
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 bg-[#EBC9B5] text-[#1F3864] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide hover:bg-[#FFFFFF] active:scale-95 transition-[background,transform]">
                  + Add date
                </button>
              )}
              {type === "studio" && staged.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {staged.map((e, i) => (
                    <div key={i} data-testid={`staged-${i}`} className="flex items-center justify-between gap-2 text-xs text-[#EBE6E0] bg-[#EBE6E0]/5 border border-[#EBE6E0]/10 rounded-lg px-3 py-2">
                      <span>{new Date(e.date + "T00:00:00").toLocaleDateString("default", { month: "short", day: "numeric" })} · {PASSES.find((p) => p.id === e.pass_type)?.label} · {SESSION_LABEL[e.session]}{e.pass_type === "overnight" ? ` · ${e.guests} guest(s)` : ""}</span>
                      <span className="font-bold text-[#D7E85C]">{fmtMoney(symbol, entryPrice(e))}</span>
                      <button type="button" onClick={() => setStaged(staged.filter((_, j) => j !== i))} className="text-[#FF8473] hover:text-[#ffb3a6]">×</button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#EBE6E0]/50 mt-4 mb-2">Got a discount code?</p>
              <input data-testid="booking-code" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. RADA10" className={`${inputCls} uppercase`} />

              <div data-testid="booking-total" className="mt-4 flex items-center justify-between rounded-lg bg-[#D7E85C]/10 border border-[#D7E85C]/25 px-3.5 py-2.5">
                <span className="text-xs font-bold uppercase tracking-widest text-[#EBE6E0]/70">Total{staged.length > 0 ? ` · ${staged.length} date(s)` : ""}</span>
                <span className="font-display font-black text-2xl text-[#D7E85C]">
                  {fmtMoney(symbol, grandTotal)}
                  {disc ? <span className="ml-2 text-xs text-[#EBE6E0]/60 line-through">{fmtMoney(symbol, Math.round((stagedTotal + currentPrice) * 100) / 100)}</span> : null}
                </span>
              </div>
            </>
          )}
        </div>

        {/* details */}
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input data-testid="booking-name" className={inputCls} placeholder="Your name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="booking-email" type="email" className={inputCls} placeholder="you@company.com"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <textarea data-testid="booking-note" rows={5} className={`${inputCls} resize-none flex-1`}
            placeholder="A brief note — what would you like to talk about or make?"
            value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <button type="submit" data-testid="booking-submit" disabled={busy}
            className="inline-flex items-center justify-center gap-3 bg-[#D7E85C] text-[#1F3864] py-3 px-8 rounded-full font-bold uppercase text-sm tracking-wide hover:bg-[#EBE6E0] active:scale-95 transition-[background,transform] duration-200 disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Booking…</>
              : <>{type === "meeting" ? (customTime ? "Reserve chosen slot" : "Request free consult") : "Reserve pass"}<ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
};
